import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import MaskedInput from 'react-text-mask';
import { styled } from '@mui/system';
import MuiGrid from '@mui/material/Grid';
import CustomInputBase from '@cpg-its/ads.form.custom-input-base';
import Icon from '@cpg-its/ads.common.icon';
import InputLabel from '@cpg-its/ads.form.input-label';
import Select from '@cpg-its/ads.form.select';
import SearchSuggest from '@cpg-its/ads.form.search-suggest';
import Typography from '@cpg-its/ads.common.typography';
import { blue, darkGrey } from '@cpg-its/ads.constants.colors';
const CustomMaskedInput = forwardRef(({ inputRef, mask, ...other }, ref) => {
    // Function to handle the ref assignment
    const handleRef = (maskedRef) => {
        const inputElement = maskedRef?.inputElement;
        // Handle inputRef assignment
        if (typeof inputRef === 'function') {
            inputRef(inputElement);
        }
        else if (inputRef) {
            const inputRefObject = inputRef;
            inputRefObject.current = inputElement;
        }
        // Handle ref assignment
        if (typeof ref === 'function') {
            ref(inputElement);
        }
        else if (ref) {
            const refObject = ref;
            refObject.current = inputElement;
        }
    };
    return _jsx(MaskedInput, { ...other, ref: (maskedRef) => handleRef(maskedRef), mask: mask });
});
CustomMaskedInput.displayName = 'CustomMaskedInput';
const overrideStyles = ({ color }) => ({
    color: {
        border: `1px solid ${color}`,
        color,
        '& .MuiInputBase-input:focus': {
            border: `1px solid ${color}`,
        },
        '&:hover': {
            border: `1px solid ${color}`,
        },
        '& input, textarea': {
            color,
        },
    },
    narrow: {
        height: '2rem',
        '& input': {
            height: '1rem',
            margin: '2px 1px',
            padding: '5px 0 5px 6px',
        },
    },
    narrowIcon: {
        bottom: '26px !important',
    },
    thickBorder: {
        border: `2px solid ${color || blue} !important`,
    },
    iconPadding: {
        '& input, textarea': {
            paddingRight: '2rem',
        },
    },
});
const IconWrapper = styled(MuiGrid)({
    position: 'relative',
    float: 'right',
    bottom: '30px',
    right: '8px',
});
const LengthWrapper = styled(MuiGrid)({
    paddingTop: '0.25rem',
});
export const InputComponent = ({ beenTouched, color, description, disabled, displayMaxLength, errorStyles, field: { name, value, onChange: fieldOnChange, onBlur: fieldOnBlur, } = {}, form: { setFieldTouched, setFieldValue } = {}, hasError, icon, iconAction, iconColor, label, labelColor, mask, maxLength, multiline, multiple, multipleHeight, narrow, native, noDefault, onBlur, onChange, onFocus, onKeyPress, onPaste, options, placeholder, published, required, rows, searchAction, searchSuggest, select, subLabel, testId, thickBorder, tooltip, type, }) => {
    const styles = overrideStyles({
        color,
    });
    const inputSx = [
        beenTouched && hasError && errorStyles ? errorStyles.errorInput : {},
        color ? styles.color : {},
        narrow ? styles.narrow : {},
        thickBorder ? styles.thickBorder : {},
        icon ? styles.iconPadding : {},
    ];
    const handleBlur = (event) => {
        if (onBlur) {
            onBlur(event);
        }
        else if (fieldOnBlur) {
            fieldOnBlur(event);
        }
    };
    const handleChange = (event) => {
        if (onChange) {
            onChange(event);
        }
        else if (fieldOnChange) {
            fieldOnChange(event);
        }
    };
    const handleFocus = (event) => {
        if (onFocus) {
            onFocus(event);
        }
    };
    const handleKeyPress = (event) => {
        if (onKeyPress) {
            onKeyPress(event);
        }
    };
    const handleOnPaste = (event) => {
        if (onPaste) {
            event.stopPropagation();
            event.preventDefault();
            onPaste(event);
        }
    };
    const inputLabel = (() => {
        if (label || subLabel || required) {
            return (_jsx(InputLabel, { color: labelColor, label: label, published: published, required: required, subLabel: subLabel, tooltip: tooltip }));
        }
        return null;
    })();
    const renderIcon = (() => {
        if (icon) {
            return (_jsx(IconWrapper, { sx: narrow ? styles.narrowIcon : {}, onClick: iconAction, children: _jsx(Icon, { color: iconColor, icon: icon }) }));
        }
        return null;
    })();
    const renderLengthCount = (() => {
        if (displayMaxLength && maxLength && typeof value === 'string') {
            return (_jsx(LengthWrapper, { container: true, item: true, xs: description ? 1 : 12, justifyContent: "flex-end", children: _jsx(Typography, { text: `${value.length}/${maxLength}`, size: "small" }) }));
        }
        return null;
    })();
    const renderInputDescription = (() => {
        if (description) {
            return (_jsx(MuiGrid, { item: true, xs: displayMaxLength && maxLength ? 11 : 12, children: _jsx(Typography, { text: description, color: darkGrey, variant: "caption" }) }));
        }
        return null;
    })();
    const inputBase = (_jsxs(_Fragment, { children: [_jsx(CustomInputBase, { sx: inputSx, "data-formdata": name, disabled: disabled, inputProps: {
                    'data-testid': testId,
                    maxLength: maxLength || null,
                }, multiline: multiline, name: name, onBlur: handleBlur, onChange: handleChange, onFocus: handleFocus, onKeyDown: handleKeyPress, onPaste: handleOnPaste, placeholder: placeholder, rows: rows, type: type, value: value || '', fullWidth: true }), renderIcon] }));
    const inputField = (() => {
        let inputMask = mask;
        if (mask) {
            if (mask === 'clientNumber') {
                inputMask = [/[1-9]/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/];
            }
            if (mask === 'ssn') {
                inputMask = [/[1-9]/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
            }
            return (_jsx(CustomInputBase, { sx: inputSx, "data-formdata": name, disabled: disabled, name: name, onBlur: handleBlur, onChange: handleChange, onFocus: handleFocus, onKeyDown: handleKeyPress, placeholder: placeholder, type: type, value: value || '', inputComponent: CustomMaskedInput, inputProps: {
                    'data-testid': testId,
                    mask: inputMask,
                }, fullWidth: true }));
        }
        if (searchSuggest) {
            return (_jsx(SearchSuggest, { beenTouched: beenTouched, color: color, disabled: disabled, errorStyles: errorStyles, hasError: hasError, icon: icon, iconColor: iconColor, name: name, narrow: narrow, options: options, onBlur: onBlur, onChange: onChange, placeholder: placeholder, searchAction: searchAction, searchSuggest: searchSuggest, setFieldTouched: setFieldTouched, setFieldValue: setFieldValue, testId: testId, thickBorder: thickBorder, value: value }));
        }
        if (select) {
            return (_jsx(Select, { beenTouched: beenTouched, color: color, disabled: disabled, errorStyles: errorStyles, hasError: hasError, multiple: multiple, multipleHeight: multipleHeight, name: name, narrow: narrow, native: native, noDefault: noDefault, onBlur: onBlur, onChange: onChange, onFocus: onFocus, options: options, overrideStyles: overrideStyles, placeholder: placeholder, setFieldTouched: setFieldTouched, setFieldValue: setFieldValue, testId: testId, thickBorder: thickBorder, value: value }));
        }
        return inputBase;
    })();
    return (_jsxs(_Fragment, { children: [inputLabel, inputField, (description || displayMaxLength) && (_jsxs(MuiGrid, { container: true, children: [renderInputDescription, renderLengthCount] }))] }));
};
export default InputComponent;
//# sourceMappingURL=input.js.map